import { useCallback, useEffect, useRef, useState } from 'react';
import { reportProctoring } from '../services/exams.service';

// Advisory proctoring for an Official exam. Everything here can only DETER and LOG — never
// prevent. A browser cannot stop cheating: the OS intercepts most screenshot keystrokes before
// the page sees them, and a phone camera is invisible to a web page entirely. So the counts are
// a floor, not a measurement, and nothing in this hook blocks, fails or re-grades an attempt —
// the numbers go to the federation admin, who judges them.
//
// An Official exam is held in FULLSCREEN and every exit is counted. Fullscreen is genuinely
// awkward — the Fullscreen API is unavailable on iOS Safari, and a user who merely switches app
// can drop out of it — which is why leaving is counted rather than blocked, and why the overlay
// offers a way back in instead of ending the sitting. Where the API is missing entirely, the
// other two signals still work and the fullscreen tally simply stays at zero.

const STORAGE_PREFIX = 'proref.proctor';

export interface ProctoringState {
  /** Running totals for this attempt (survives a refresh). */
  focusLostCount: number;
  screenshotAttempts: number;
  fullscreenExitCount: number;
  /** True once the candidate has left the exam and hasn't acknowledged coming back. */
  warning: boolean;
  /** True while the exam should be in fullscreen but isn't — the overlay stays up until it is. */
  needsFullscreen: boolean;
  /**
   * Dismiss the warning and (re-)enter fullscreen. MUST be called from a click handler: the
   * Fullscreen API only grants a request made during a user gesture, which is the whole reason
   * this is a button on an overlay rather than something the hook can do on its own.
   */
  acknowledge: () => void;
}

// Whether this browser can do fullscreen at all. iOS Safari cannot on a non-video element, so
// the enforcement is skipped there rather than trapping the candidate behind an overlay whose
// button can never succeed.
function fullscreenSupported(): boolean {
  return typeof document !== 'undefined' && typeof document.documentElement?.requestFullscreen === 'function';
}

// Screenshot shortcuts the browser actually surfaces. `code` is layout-independent, so this
// works on a Croatian keyboard as well as a US one.
function isScreenshotChord(e: KeyboardEvent): boolean {
  const mac = e.metaKey && e.shiftKey && ['Digit3', 'Digit4', 'Digit5'].includes(e.code);
  const winSnip = e.metaKey && e.shiftKey && e.code === 'KeyS';
  return mac || winSnip;
}

export function useExamProctoring(attemptId: string | undefined, enabled: boolean): ProctoringState {
  const [counts, setCounts] = useState({ focusLostCount: 0, screenshotAttempts: 0, fullscreenExitCount: 0 });
  const [warning, setWarning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Mirrors `counts` for the event listeners, which are registered once and must not re-bind on
  // every increment (re-binding mid-blur would drop events).
  const countsRef = useRef(counts);
  // "Already counted as away" latch — blur and visibilitychange both fire for one alt-tab, and
  // one departure is one violation, not two.
  const awayRef = useRef(false);

  const storageKey = attemptId ? `${STORAGE_PREFIX}.${attemptId}` : null;

  // Restore the running totals on mount so a refresh (or a resumed attempt) doesn't hand the
  // candidate a clean slate — reloading the page would otherwise erase every violation.
  useEffect(() => {
    if (!enabled || !storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<typeof counts>;
      const restored = {
        focusLostCount: Number(parsed.focusLostCount) || 0,
        screenshotAttempts: Number(parsed.screenshotAttempts) || 0,
        fullscreenExitCount: Number(parsed.fullscreenExitCount) || 0,
      };
      countsRef.current = restored;
      setCounts(restored);
    } catch {
      // Malformed or unavailable storage (private mode) — start from zero rather than throwing
      // an exception into the middle of someone's exam.
    }
  }, [enabled, storageKey]);

  // Persist locally AND report to the server. Fire-and-forget in both directions: a failed
  // report must never surface to the candidate or interrupt the exam.
  const persist = useCallback(() => {
    if (!attemptId || !storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(countsRef.current));
    } catch {
      // ignore storage failures
    }
    void reportProctoring(attemptId, {
      focus_lost_count: countsRef.current.focusLostCount,
      screenshot_attempts: countsRef.current.screenshotAttempts,
      fullscreen_exit_count: countsRef.current.fullscreenExitCount,
    }).catch(() => {
      // The server takes the max of stored and reported, so the next report carries this one's
      // total anyway — a lost request costs nothing.
    });
  }, [attemptId, storageKey]);

  const bump = useCallback(
    (field: 'focusLostCount' | 'screenshotAttempts' | 'fullscreenExitCount') => {
      countsRef.current = { ...countsRef.current, [field]: countsRef.current[field] + 1 };
      setCounts(countsRef.current);
      persist();
    },
    [persist],
  );

  // Leaving the exam: blur + visibilitychange together cover alt-tab, minimise, switching app
  // and locking the screen. The warning stays up until the candidate acknowledges it, so it
  // can't be missed by returning and immediately clicking on.
  useEffect(() => {
    if (!enabled) return;

    const onLeave = () => {
      if (awayRef.current) return;
      awayRef.current = true;
      bump('focusLostCount');
      setWarning(true);
    };
    const onReturn = () => {
      awayRef.current = false;
    };
    const onVisibility = () => (document.hidden ? onLeave() : onReturn());

    window.addEventListener('blur', onLeave);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', onLeave);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, bump]);

  // Screenshots + copying.
  useEffect(() => {
    if (!enabled) return;

    const onKeyUp = (e: KeyboardEvent) => {
      // Windows swallows PrintScreen's keydown but lets keyup through, so this one is caught on
      // the way up. Overwriting the clipboard afterwards is the only lever a page has against a
      // PrtSc grab actually being pasted somewhere.
      if (e.key === 'PrintScreen') {
        bump('screenshotAttempts');
        void navigator.clipboard?.writeText(' ').catch(() => {
          // Clipboard access is often blocked without a user gesture — best effort only.
        });
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (isScreenshotChord(e)) bump('screenshotAttempts');
    };
    // Copying the question text out is the low-effort exfiltration path, so it's blocked
    // outright rather than counted.
    const prevent = (e: Event) => e.preventDefault();

    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('contextmenu', prevent);
    document.addEventListener('copy', prevent);
    document.addEventListener('cut', prevent);
    return () => {
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('contextmenu', prevent);
      document.removeEventListener('copy', prevent);
      document.removeEventListener('cut', prevent);
    };
  }, [enabled, bump]);

  // Fullscreen: enter on start and count every exit. Requesting on mount usually succeeds because
  // the click that started the exam is still the active user gesture; when it doesn't (a resumed
  // attempt, a refresh), the overlay's button provides a fresh gesture.
  useEffect(() => {
    if (!enabled || !fullscreenSupported()) return;

    const onChange = () => {
      const active = Boolean(document.fullscreenElement);
      setIsFullscreen(active);
      // Only an EXIT counts. Entering (including the retry from the overlay) must not.
      if (!active) bump('fullscreenExitCount');
    };
    // Listen BEFORE requesting. Browsers fire fullscreenchange in a later task so the reverse
    // order happens to work, but that is an ordering guarantee worth not depending on — subscribe
    // first and the transition cannot be missed.
    document.addEventListener('fullscreenchange', onChange);

    setIsFullscreen(Boolean(document.fullscreenElement));
    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen().catch(() => {
        // No user gesture available — the overlay stays up and its button retries.
      });
    }
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      // Leaving the exam must not strand the rest of the app in fullscreen.
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    };
  }, [enabled, bump]);

  // The overlay covers the exam while it is out of fullscreen, and after a screen-leave until the
  // candidate acknowledges. Both conditions clear through the same button.
  const needsFullscreen = enabled && fullscreenSupported() && !isFullscreen;

  const acknowledge = useCallback(() => {
    // Re-enter fullscreen first: this call is inside the click handler, so the gesture is live.
    if (fullscreenSupported() && !document.fullscreenElement) {
      void document.documentElement.requestFullscreen().catch(() => {});
    }
    setWarning(false);
  }, []);

  return { ...counts, warning, needsFullscreen, acknowledge };
}

// Clear an attempt's stored tallies once it's submitted — the server holds the record from then
// on, and leaving the key behind would let a stale total ride along with a retake.
export function clearProctoringStorage(attemptId: string): void {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}.${attemptId}`);
  } catch {
    // ignore
  }
}
